import type { TerminalSession } from '@/components/desktop-panel-primitives';
import type { AnnotateSelectionInput } from '@/components/desktop-side-panel/file-selection-toolbar';
import type { PanelSplitRect } from '@/components/desktop-side-panel/panel-split-frame';
import type { BrowserSelection } from '@/components/native-browser-view';
import type { BrowserSession, BrowserSessionMode } from '@/lib/side-panel/browser-session';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';
import { pluginService } from './plugin-services';
/**
 * The tabs of a surface the panel shows, by id: filling the view alone (`null`), or laid over
 * their cell of the split (its box in the panel's views). The others are not shown.
 */
export type SurfacePlacements = ReadonlyMap<string, PanelSplitRect | null>;

export type BrowserSurface = {
  sessions: readonly BrowserSession[];
  visible: boolean;
  /** The tab shown last, which the sidebar marks. */
  activeBrowserId: string;
  placements: SurfacePlacements;
  controlledBrowserIds: ReadonlySet<string>;
  workspace: string | null;
  onBrowserSelection: (selection: BrowserSelection) => void;
  onRenameSession: (id: string, name: string) => void;
  onSessionFavicon: (id: string, favicon: string | null) => void;
  /** Moves a tab to another profile (shared, isolated, private), on the page it shows. */
  onSessionModeChange: (id: string, mode: BrowserSessionMode) => void;
  /** Shows a tab (from the tabs the sidebar lists, or clicked in the split). */
  onSelectSession: (id: string) => void;
  /** Opens a new, blank tab and shows it (⌘T in a page). */
  onNewSession: () => void;
  /** Closes a tab (⌘W in its page). */
  onCloseSession: (id: string) => void;
};

export type TerminalSurface = {
  sessions: readonly TerminalSession[];
  placements: SurfacePlacements;
  /** Shows a tab (clicked in the split). */
  onSelectSession: (id: string) => void;
  client: WorkspaceClient;
  workspace: string;
  onAskSelectionInSideChat: (content: string) => void;
  onMoreDetailsSelection: (content: string) => void;
  onAnnotateSelection: (input: AnnotateSelectionInput) => void;
  onRenameSession: (id: string, name: string) => void;
};

export const webSurface = pluginService<BrowserSurface>('platform.web-surface');
export const terminalSurface = pluginService<TerminalSurface>('platform.terminal-surface');
